// Canonical athlete level options — single source of truth (frontend mirror).
//
// Used by:
//   - frontend/src/pages/Profile.jsx (athlete self-edit, getLevelLabel)
//   - frontend/src/pages/CoachDashboard.jsx (coach editing athlete profile)
//   - frontend/src/utils/goalValidator.js (km × level × day validation)
//
// RUNNER-LEVEL MILEAGE POLICY (2026-08-02): athlete-facing running levels are
// BEGINNER / EXPERIENCED / ADVANCED (the old "Intermediate" is renamed
// "Experienced"). The mileage bands below MUST equal the backend authority
// RUNNER_LEVEL_MILEAGE_POLICY in generator_v2/fixed_templates/runner_level_policy.py.
// Parity is enforced by test_frontend_backend_parity.py — any divergence fails CI.
//
// Cycling is a separate, isolated sport policy (4 levels) and is unchanged.

export const RUNNING_LEVEL_OPTIONS = [
  { value: 'beginner',    label: 'מתחיל (0–25 ק"מ)' },
  { value: 'experienced', label: 'מנוסה (26–40 ק"מ)' },
  { value: 'advanced',    label: 'מתקדם (41+ ק"מ)' },
];

// Distance-scoped runner-level mileage bands (km). max:null = no level ceiling
// (still constrained by the Profile Envelope for the selected training days).
export const RUNNER_LEVEL_MILEAGE_POLICY = {
  '10K': {
    beginner:    { min: 0,  max: 25 },
    experienced: { min: 26, max: 40 },
    advanced:    { min: 41, max: null },
  },
  // 5K / HALF_MARATHON / MARATHON intentionally absent → DISTANCE_POLICY_NOT_CONFIGURED.
};
export const CONFIGURED_DISTANCES = ['10K'];

// ── THE HARD 10K PRODUCT ENVELOPE ────────────────────────────────────────────
// training days → [min, max] weekly km, inclusive. THE eligibility rule: outside
// it a profile cannot be saved or generated, inside it always can.
//
// MUST equal backend `ten_k_base_table.TEN_K_PRODUCT_ENVELOPE` —
// `test_frontend_backend_parity` parses this array and fails CI on any
// divergence. The two sides had drifted before: this file blocked 5D at 32-34 km
// while the backend served it, and offered 6D from 36 km after the backend had
// moved six days to 45. One table, both repos, checked on every run.
//
// Six days start at 45 km and three days end at 35 (User 2026-08-16). Both bounds are
// product judgements about how a week divides, not arithmetic limits: six days at 36 km
// leaves too many 4-6 km runs, and three days above 35 forces a long run that is barely
// larger than the week's single easy run.
export const TEN_K_PRODUCT_ENVELOPE = {
  3: [25, 35],
  4: [27, 49],
  5: [32, 80],
  6: [45, 80],
};

// RECOMMENDATION ONLY — never eligibility. These colour the UI and suggest a
// better day count; `envelopeStatus10K` consults them only AFTER the hard
// envelope has already said the combination is offered, so a band cannot block
// a legal profile or admit an illegal one.
//
// They are LEFT AT THEIR 2026-08-02 VALUES on purpose. They mirror
// `generator_v2/fixed_templates/profile_envelope.py`, whose certified matrix,
// geometry audit and quality-count suites are all built on these exact numbers —
// re-cutting them to hug the new hard envelope broke six of those modules. The
// overlap that remains (3D from 24, 6D 36-41) is now unreachable: the hard
// envelope refuses those cells before this table is read. Aligning the two is a
// separate piece of work on generator_v2, not a prerequisite for the envelope.
export const PROFILE_ENVELOPE = {
  '10K': {
    3: { green: [24, 35], yellowHi: [36, 40], yellowLo: null, recDays: 4 },   // 24 approved 2026-08-05
    4: { green: [28, 48], yellowHi: [49, 52], yellowLo: null, recDays: 5 },
    5: { green: [35, 60], yellowHi: [61, 65], yellowLo: null, recDays: 6 },
    6: { green: [42, 75], yellowHi: null,     yellowLo: [36, 41], recDays: 5 },
  },
};

export const CYCLING_LEVEL_OPTIONS = [
  { value: 'beginner',     label: 'מתחיל' },
  { value: 'intermediate', label: 'בינוני' },
  { value: 'advanced',     label: 'מתקדם' },
  { value: 'professional', label: 'מקצועי' },
];

// Display-side mapping. Legacy running value 'intermediate' now shows as
// 'מנוסה' (Experienced) — never 'בינוני' for the active running product.
const _LEVEL_LABELS = {
  beginner:     'מתחיל',
  experienced:  'מנוסה',
  advanced:     'מתקדם',
  professional: 'מקצועי',
  // Legacy values that may still exist in old rows (read-mapping only, never rewritten):
  intermediate: 'מנוסה',   // running legacy → Experienced
  competitive:  'תחרותי',
};
const _RUNNING_LEVEL_ALIASES = { intermediate: 'experienced', inter: 'experienced', int: 'experienced' };

export const LEVEL_OPTIONS = RUNNING_LEVEL_OPTIONS;   // backwards-compat default

export function getLevelLabel(value) {
  if (!value) return '';
  return _LEVEL_LABELS[value] || value;
}

// Normalize a stored running level for READING (legacy 'intermediate' → 'experienced').
// Does NOT rewrite the stored value — persistence is confirmed explicitly by the user.
export function normalizeRunningLevel(value) {
  const v = String(value || '').toLowerCase();
  return _RUNNING_LEVEL_ALIASES[v] || v;
}

// UX helper mirroring the backend resolver: for a 10K weekly volume, which level matches?
export function suggestedRunningLevelFor10K(weeklyKm) {
  const bands = RUNNER_LEVEL_MILEAGE_POLICY['10K'];
  for (const lvl of ['beginner', 'experienced', 'advanced']) {
    const { min, max } = bands[lvl];
    if (weeklyKm >= min && (max === null || weeklyKm <= max)) return lvl;
  }
  return null;
}

// Validate a declared 10K (level × weekly km). Returns {status, allowedMinKm, allowedMaxKm, suggested}.
export function validateRunnerLevelMileage10K(level, weeklyKm) {
  const norm = normalizeRunningLevel(level);
  const band = (RUNNER_LEVEL_MILEAGE_POLICY['10K'] || {})[norm];
  if (!band) return { status: 'INVALID', allowedMinKm: null, allowedMaxKm: null, suggested: suggestedRunningLevelFor10K(weeklyKm) };
  const ok = weeklyKm >= band.min && (band.max === null || weeklyKm <= band.max);
  return { status: ok ? 'VALID' : 'INVALID', allowedMinKm: band.min, allowedMaxKm: band.max,
           suggested: ok ? null : suggestedRunningLevelFor10K(weeklyKm) };
}
